/**
 * RoofM2ConfidenceBanner — Sprint S3 · 2026-02c
 *
 * Variance-aware wrapper around <ConfidenceBanner> for the roof-m²
 * chip on each builder header. Fetches
 *   GET /api/appraisals/{id}/roof-m2/variance
 * once per (api, appraisalId) and forwards `variancePct` + `tolerancePct`
 * to <ConfidenceBanner> so the chip tints amber (>tolerance) or rose
 * (>2× tolerance) whenever the Sheets-Components m² diverges from the
 * Roof-Details m² — even when the underlying source is `confirmed`.
 *
 * Reads the same `roof_m2_calibration_log` collection the admin drawer
 * feeds off (backend upserts one sample per appraisal on every fetch).
 *
 * Usage — drop-in replacement:
 *   <ConfidenceBanner source={...} confidence={...} label="Roof m²" />
 * becomes
 *   <RoofM2ConfidenceBanner api={api} appraisalId={id}
 *                           source={...} confidence={...} label="Roof m²" />
 */
import React, { useEffect, useState } from 'react';
import { ConfidenceBanner } from '@/components/ConfidenceBanner';

export function RoofM2ConfidenceBanner({ api, appraisalId, source, confidence, label = 'Roof m²', compact = false, className = '' }) {
  const [variance, setVariance] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!api || !appraisalId) return () => { cancelled = true; };
    (async () => {
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}/roof-m2/variance`);
        if (!cancelled) setVariance(data);
      } catch (_e) {
        // Silent — variance is advisory · never blocks the source chip
        if (!cancelled) setVariance(null);
      }
    })();
    return () => { cancelled = true; };
  }, [api, appraisalId]);

  return (
    <ConfidenceBanner
      source={source}
      confidence={confidence}
      label={label}
      compact={compact}
      className={className}
      variancePct={variance?.variance_pct ?? null}
      tolerancePct={variance?.tolerance_pct ?? 5}
    />
  );
}
